/* =========================================================================
   Telemetry Enum & Status Badge Utilities
   Maps raw telemetry values against parameter enum dictionaries,
   formats uppercase string labels, and generates ON/OFF badges.
   ========================================================================= */

import { TelemetryCatChannelType, TelemetryUnitSystem } from '../types/telemetry-dashboard.types';
import { unitOf, fmt } from './telemetry-unit.utils';

/**
 * Resolves raw telemetry value against parameter enumValues dictionary or array.
 * Covers all backend data formats:
 * - Numbers: 0, 1, 0.0, 1.0, floats
 * - Strings: "0", "1", "0.0", "1.0", "ON", "OFF", "ADCS_COMP_ON", "true", "false"
 * - Booleans: true, false
 * - Arrays: [1, 2, 4], ["0", "1"] (multi-series channels)
 * - Array enumValues: ["OFF", "ON"]
 * - Object enumValues: {"0": "OFF", "1": "ON"}, {"off": "OFF", "on": "ON"}
 * Returns uppercase string if match found, otherwise null.
 */
export function resolveEnumValue(
  val: number | string | boolean | any[] | null | undefined,
  enumValues?: Record<string, unknown> | Array<unknown> | null
): string | null {
  if (val === null || val === undefined || !enumValues || typeof enumValues !== 'object') {
    return null;
  }

  // Guard against empty or whitespace-only strings to prevent false 0 coercion
  if (typeof val === 'string' && val.trim() === '') {
    return null;
  }

  // 1. Handle arrays for multi-series channels (e.g. [1, 2, 4] or ["0", "1"])
  if (Array.isArray(val)) {
    const mapped = val.map((item) => {
      const resolved = resolveEnumValue(item, enumValues);
      return resolved !== null ? resolved : String(item);
    });
    return mapped.join(', ');
  }

  // 2. Handle boolean inputs (true -> "true", "1", 1; false -> "false", "0", 0)
  if (typeof val === 'boolean') {
    const boolKey = val ? 'true' : 'false';
    const numKey = val ? '1' : '0';
    return (
      resolveEnumValue(numKey, enumValues) ||
      resolveEnumValue(boolKey, enumValues) ||
      (val ? 'True' : 'False')
    );
  }

  // 3. Handle enumValues as Array (e.g. ["OFF", "ON", "INVALID"])
  if (Array.isArray(enumValues)) {
    const num = typeof val === 'number' ? val : Number(val);
    if (!isNaN(num) && Number.isInteger(Math.round(num))) {
      const idx = Math.round(num);
      if (idx >= 0 && idx < enumValues.length && enumValues[idx] != null) {
        return String(enumValues[idx]);
      }
    }
    // Check if val is already one of the items in the array (case-insensitive)
    const strVal = String(val).trim().toLowerCase();
    const found = enumValues.find((item) => String(item).trim().toLowerCase() === strVal);
    if (found != null) {
      return String(found);
    }
    return null;
  }

  // 4. Handle enumValues as Record / Dictionary object
  const dict = enumValues as Record<string, unknown>;

  // 4a. Direct string key lookup (e.g. "0", "1", "ON", "OFF")
  const strVal = String(val).trim();
  if (Object.prototype.hasOwnProperty.call(dict, strVal)) {
    const res = dict[strVal];
    return res != null ? String(res) : null;
  }

  // 4b. Case-insensitive key lookup (e.g. key is "off" or "OFF" or "False")
  const strLower = strVal.toLowerCase();
  const matchedKey = Object.keys(dict).find((k) => k.trim().toLowerCase() === strLower);
  if (matchedKey && dict[matchedKey] != null) {
    return String(dict[matchedKey]);
  }

  // 4c. Numeric lookup for numbers / floats (e.g. 0.0 -> "0", 1.0 -> "1", "0.0" -> "0")
  const num = typeof val === 'number' ? val : Number(val);
  if (!isNaN(num)) {
    const intKey = String(Math.round(num));
    if (Object.prototype.hasOwnProperty.call(dict, intKey)) {
      const res = dict[intKey];
      return res != null ? String(res) : null;
    }
  }

  // 4d. Value match: If telemetry data is already the enum value (e.g. val = "ADCS_COMP_ON")
  const matchedVal = Object.values(dict).find(
    (v) => v != null && String(v).trim().toLowerCase() === strLower
  );
  if (matchedVal != null) {
    return String(matchedVal);
  }

  return null;
}

/**
 * Checks if a string represents an 'ON' or 'OFF' boolean state.
 */
export function isBooleanEnumState(str?: string | null): 'on' | 'off' | null {
  if (!str) return null;
  const s = String(str).trim().toLowerCase();
  if (s === 'on') return 'on';
  if (s === 'off') return 'off';
  return null;
}

export interface FormattedTelemetryValue {
  text: string;
  isEnum: boolean;
  enumState: 'on' | 'off' | null;
  unit: string;
}

/**
 * Unified Formatter with Enum & Status Badge metadata.
 */
export function formatTelemetryValue(
  val: number | null | undefined,
  def: TelemetryCatChannelType,
  units: TelemetryUnitSystem = {},
  precision: number = 2
): FormattedTelemetryValue {
  const enumVal = resolveEnumValue(val, def?.enumValues);
  if (enumVal) {
    const rawValStr = val != null ? ` (${val})` : '';
    return {
      text: `${enumVal}${rawValStr}`,
      isEnum: true,
      enumState: isBooleanEnumState(enumVal),
      unit: '', // Suppress unit for enum strings
    };
  }

  const formatted = fmt(val, def, units, precision);
  return {
    text: formatted,
    isEnum: false,
    enumState: null,
    unit: unitOf(def, units) || def?.unit || '',
  };
}

/**
 * Generates inline HTML badge for ECharts Tooltips with optional raw value.
 */
export function makeEnumBadgeHtml(
  text: string,
  state: 'on' | 'off' | null,
  rawVal?: number | string | null
): string {
  const rawSuffix = rawVal !== undefined && rawVal !== null
    ? `<span style="opacity:0.75;font-size:10.5px;margin-left:5px;font-family:var(--font-mono, monospace);">(${rawVal})</span>`
    : '';

  if (!state) return `${text}${rawSuffix}`;
  const isOff = state === 'off';
  const bg = isOff ? 'rgba(242, 244, 247, 0.15)' : 'rgba(52, 199, 89, 0.18)';
  const color = isOff ? '#94A3B8' : '#22C55E';
  const border = isOff ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid rgba(52, 199, 89, 0.35)';
  const badgeHtml = `<span style="display:inline-flex;align-items:center;justify-content:center;padding:1px 7px;border-radius:4px;font-size:10px;font-weight:700;letter-spacing:0.5px;background:${bg};color:${color};border:${border};text-transform:uppercase;">${text}</span>`;
  return `${badgeHtml}${rawSuffix}`;
}
